/**
 * The storefront cart — pure, storage-backed state (no React in this module so
 * it is directly unit-testable, mirroring the -view/-container seam).
 *
 * Whole-listing purchase (the 4E.4 checkout model): each cart line is ONE
 * listing (no quantities — `quantity_available` is display metadata, not the
 * reserve unit). The cart persists in localStorage so it survives the PKCE
 * login redirect round-trip; the line carries a small display snapshot
 * (headline/price/currency) so the cart screen renders offline of the catalog.
 *
 * Idempotency (the enshrined 4B.8 rule, client side): the checkout
 * `client_op_id` is minted once per distinct cart content and kept in
 * sessionStorage until that cart's checkout resolves — so a retry of a failed
 * network round-trip REPLAYS the same operation (the server returns the stored
 * receipt if the first attempt actually committed), while any cart change
 * mints a fresh identity (the server would 409 a divergent reuse).
 */

export interface CartLine {
  /** the listing id — the checkout unit */
  id: string;
  /** display snapshot (from the public catalog projection) */
  headline: string | null;
  ask_price: number | null;
  currency: string | null;
}

const CART_KEY = 'pipe.storefront.cart';
const OP_KEY = 'pipe.storefront.checkout_op';

// ---- pure list operations ---------------------------------------------------

/** Add a line (dedup by listing id — whole-listing purchase, one line each). */
export function addLine(lines: CartLine[], line: CartLine): CartLine[] {
  if (lines.some(l => l.id === line.id)) {
    return lines;
  }
  return [...lines, line];
}

export function removeLine(lines: CartLine[], id: string): CartLine[] {
  return lines.filter(l => l.id !== id);
}

export function hasLine(lines: CartLine[], id: string): boolean {
  return lines.some(l => l.id === id);
}

/**
 * The display total, or null when any line is missing a price (the server
 * recomputes the authoritative total at checkout — this is presentation only).
 */
export function cartTotal(lines: CartLine[]): number | null {
  let total = 0;
  for (const l of lines) {
    if (typeof l.ask_price !== 'number') {
      return null;
    }
    total += l.ask_price;
  }
  return total;
}

/**
 * A canonical content key for the cart — the SORTED listing ids (matching the
 * server's canonical idempotency preimage ordering).
 */
export function cartKey(lines: CartLine[]): string {
  return lines
    .map(l => l.id)
    .sort()
    .join(',');
}

// ---- persistence -------------------------------------------------------------

export function loadCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (l): l is CartLine =>
        typeof l === 'object' &&
        l !== null &&
        typeof (l as { id?: unknown }).id === 'string'
    );
  } catch {
    return [];
  }
}

export function saveCart(lines: CartLine[]): void {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(lines));
  } catch {
    // storage unavailable (private mode) — the in-memory cart still works.
  }
}

export function clearCart(): void {
  try {
    localStorage.removeItem(CART_KEY);
  } catch {
    // ignore
  }
}

// ---- checkout op identity ------------------------------------------------------

/**
 * The client_op_id for THIS cart content: reused while the content is
 * unchanged (a network-failure retry replays the same server-side operation),
 * re-minted when the content differs (a divergent reuse would be a 409).
 */
export function ensureOpId(key: string): string {
  try {
    const raw = sessionStorage.getItem(OP_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as { key?: unknown; op?: unknown };
      if (stored.key === key && typeof stored.op === 'string') {
        return stored.op;
      }
    }
  } catch {
    // fall through to mint
  }
  const op = crypto.randomUUID();
  try {
    sessionStorage.setItem(OP_KEY, JSON.stringify({ key, op }));
  } catch {
    // storage unavailable — a one-shot id still works for this attempt.
  }
  return op;
}

export function clearOpId(): void {
  try {
    sessionStorage.removeItem(OP_KEY);
  } catch {
    // ignore
  }
}
